export type CellValue = number | string | boolean | null;

export interface CellErr { err: string }
export type EvalValue = CellValue | CellErr;
export type EvalResult = EvalValue | EvalValue[][];

export interface CellStyle {
  bold?: boolean;
  italic?: boolean;
  align?: 'left' | 'center' | 'right';
  color?: string;
  bg?: string;
  numFmt?: string;
}

export interface Cell {
  v?: CellValue;
  f?: string;
  s?: CellStyle;
}

export interface ForecastData {
  names: string[];
  labels: (string | number)[];
  history: Array<Array<number | null>>;
  quantiles: Array<Record<string, number[]>>;
  backend?: string;
  headerRow?: boolean;
  labelCol?: boolean;
}

export interface ChartSpec {
  id: string;
  type: 'bar' | 'line' | 'scatter' | 'pie' | 'area' | 'forecast';
  range: string;
  title?: string;
  sheet?: string;
  horizon?: number;
  forecast?: ForecastData;
}

export interface SheetModel {
  name: string;
  cells: Record<string, Cell>;
  colW?: Record<number, number>;
  rowH?: Record<number, number>;
  charts?: ChartSpec[];
  rows?: number;
  cols?: number;
}

export interface WorkbookModel {
  version: 2;
  sheets: SheetModel[];
  active?: number;
}

export function isErr(v: any): v is CellErr {
  return v != null && typeof v === 'object' && typeof (v as any).err === 'string';
}

export const ERR = {
  DIV0: { err: '#DIV/0!' },
  VALUE: { err: '#VALUE!' },
  REF: { err: '#REF!' },
  NAME: { err: '#NAME?' },
  NA: { err: '#N/A' },
  NUM: { err: '#NUM!' },
  CYCLE: { err: '#CYCLE!' },
} as const;

export function cellKey(r: number, c: number): string {
  return r + ':' + c;
}

export function parseKey(key: string): [number, number] {
  const i = key.indexOf(':');
  return [Number(key.slice(0, i)), Number(key.slice(i + 1))];
}

export function colName(c: number): string {
  let s = '';
  c += 1;
  while (c > 0) {
    const m = (c - 1) % 26;
    s = String.fromCharCode(65 + m) + s;
    c = Math.floor((c - 1) / 26);
  }
  return s;
}

export function colIndex(name: string): number {
  let n = 0;
  for (let i = 0; i < name.length; i++) n = n * 26 + (name.charCodeAt(i) - 64);
  return n - 1;
}

export function cellName(r: number, c: number): string {
  return colName(c) + (r + 1);
}

export function parseCellName(ref: string): [number, number] | null {
  const m = /^\$?([A-Za-z]{1,3})\$?(\d+)$/.exec(ref.trim());
  if (!m) return null;
  return [Number(m[2]) - 1, colIndex(m[1].toUpperCase())];
}

export interface RangeRef { sr: number; sc: number; er: number; ec: number }

export function parseRangeRef(ref: string): RangeRef | null {
  const parts = ref.split(':');
  const a = parseCellName(parts[0]);
  if (!a) return null;
  const b = parts.length > 1 ? parseCellName(parts[1]) : a;
  if (!b) return null;
  return {
    sr: Math.min(a[0], b[0]),
    sc: Math.min(a[1], b[1]),
    er: Math.max(a[0], b[0]),
    ec: Math.max(a[1], b[1]),
  };
}
